import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Analytics } from '@shared/entities/analytics/analytics';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { shareReplay, finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

interface Filter {
  name: string;
  values: any[];
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService extends ApiService<Analytics> {
  private cache$ = new Map<string, Observable<Analytics>>();
  private readonly CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  constructor(
    private activatedRoute: ActivatedRoute,
    private http: HttpClient
  ) {
    const masterUrl = '/analytics';
    const detailUrl = '/analytics';

    super(Analytics, masterUrl, detailUrl);
  }

  getByQuery(
    query: string,
    filters?: Record<string, any[]> | null,
    sortBy?: object | null,
    pagination?: { page: number; size: number } | null
  ): Observable<Analytics> {
    const cacheKey = JSON.stringify({ query, filters, sortBy, pagination });

    if (this.cache$.has(cacheKey)) {
      return this.cache$.get(cacheKey)!;
    }

    this.setUrlParameter('query', query);

    const standardParams = ['query', 'page', 'size', 'sort'];

    this.removeUrlParametersExcept(standardParams);

    if (filters && Object.keys(filters).length > 0) {
      Object.entries(filters)
        .filter(([, values]) => {
          if (!Array.isArray(values) || values.length === 0) {
            return false;
          }
          return values.some(
            (v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || String(v).trim() !== '')
          );
        })
        .forEach(([key, values]) => {
          const cleanValues = values.filter(
            (v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || String(v).trim() !== '')
          );
          if (cleanValues.length > 0) {
            this.setUrlParameter(key, cleanValues.map(encodeURIComponent).join(','));
          }
        });
    }

    if (sortBy && Object.keys(sortBy).length > 0) {
      const sortParams = Object.entries(sortBy)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');
      this.setUrlParameter('sort', sortParams);
    } else {
      this.removeUrlParameter('sort');
    }

    if (pagination) {
      this.setUrlParameter('page', pagination.page.toString());
      this.setUrlParameter('size', pagination.size.toString());
    } else {
      this.removeUrlParameters(['page', 'size']);
    }

    const request$ = this.findOne('').pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        setTimeout(() => this.cache$.delete(cacheKey), this.CACHE_TIMEOUT);
      })
    );

    this.cache$.set(cacheKey, request$);

    return request$;
  }

  exportToCSV(queryName: string, filters?: Record<string, any[]> | null): Observable<Blob> {
    const body: { queryName: string; filters: Filter[] } = {
      queryName,
      filters: []
    };

    if (filters && Object.keys(filters).length > 0) {
      body.filters = Object.entries(filters)
        .filter(([, values]) => {
          if (!Array.isArray(values) || values.length === 0) {
            return false;
          }
          return values.some(
            (v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || String(v).trim() !== '')
          );
        })
        .map(([key, values]) => ({
          name: key,
          values: values.filter(
            (v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || String(v).trim() !== '')
          )
        }));
    }

    return this.http.post(`${environment.apiBaseUrl}/analytics/csv`, body, {
      responseType: 'blob'
    });
  }

  exportToPDF(queryName: string, filters?: Record<string, any[]> | null): Observable<Blob> {
    const body: { queryName: string; filters: Filter[] } = {
      queryName,
      filters: []
    };

    if (filters && Object.keys(filters).length > 0) {
      body.filters = Object.entries(filters)
        .filter(([, values]) => {
          if (!Array.isArray(values) || values.length === 0) {
            return false;
          }
          return values.some(
            (v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || String(v).trim() !== '')
          );
        })
        .map(([key, values]) => ({
          name: key,
          values: values.filter(
            (v) => v !== null && v !== undefined && v !== '' && (typeof v !== 'string' || String(v).trim() !== '')
          )
        }));
    }

    return this.http.post(`${environment.apiBaseUrl}/analytics/pdf`, body, {
      responseType: 'blob'
    });
  }
}
